import CPS_LOGO from 'assets/images/cpslogo.svg'
import COMPLETE_LOGO from 'assets/images/logos/generic-complete-logo.svg'
import SMALL_LOGO from 'assets/images/logos/generic-small-logo.svg'
import _ from 'lodash'

const CPS_COMPLETE_LOGO = CPS_LOGO
const SMALL_GENERIC_LOGO: string = SMALL_LOGO
const COMPLETE_GENERIC_LOGO = COMPLETE_LOGO

/**
 * Returns the small logo for a given carrier.
 * @param carrier - The carrier for which to retrieve the logo. Defaults to an empty string.
 * @returns The URL of the small logo for the carrier, or the generic logo if the carrier is not found.
 */
const getSmallLogoByCarrier = (SMALL_LOGO_CARRIER: object = {}, carrier: string = ''): string =>
    // Use lodash.get to retrieve the URL of the small logo for the carrier from the SMALL_LOGO_CARRIER object.
    // If the carrier is not found in the object, return the SMALL_GENERIC_LOGO.
    // Convert the carrier to uppercase before retrieving the logo URL to ensure case-insensitive matching.
    _.get(SMALL_LOGO_CARRIER, carrier.toUpperCase(), SMALL_GENERIC_LOGO)

/**
 * Returns the small logo for a given flight number.
 * @param flightNumber - The flight number for which to retrieve the logo.
 * @returns The URL of the small logo for the flight number, or the generic logo if the flight number is not found.
 */
const getSmallLogoByFlightNumber = (
    SMALL_LOGO_CARRIER: object = {},
    flightNumber: string = '',
    carrier: string = ''
): string => {
    // Get the first two characters of the flight number and convert them to uppercase.
    const carrierCode = `${flightNumber.substring(0, 2).toUpperCase()}`

    if (carrierCode !== carrier) {
        return SMALL_GENERIC_LOGO
    }

    // Use lodash.get to retrieve the URL of the small logo for the carrier code from the SMALL_LOGO_CARRIER object.
    // If the carrier code is not found in the object, return the SMALL_GENERIC_LOGO.
    return _.get(SMALL_LOGO_CARRIER, carrierCode, SMALL_GENERIC_LOGO)
}

/**
 * Returns the complete logo for a given carrier.
 * @param carrier - The carrier for which to retrieve the logo. Defaults to an empty string.
 * @returns The URL of the complete logo for the carrier, or the generic logo if the carrier is not found.
 */
const getLogoByCarrier = (COMPLETE_CARRIER_LOGO: object = {}, carrier: string = ''): string =>
    // Use lodash.get to retrieve the URL of the complete logo for the carrier from the COMPLETE_LOGO_CARRIER object.
    // If the carrier is not found in the object, return the COMPLETE_GENERIC_LOGO.
    // Convert the carrier to uppercase before retrieving the logo URL to ensure case-insensitive matching.
    _.get(COMPLETE_CARRIER_LOGO, `${carrier.toUpperCase()}`, COMPLETE_GENERIC_LOGO)

const getLogoByCarriernotification = (COMPLETE_CARRIER_LOGO: object = {}, carrier: string = ''): string =>
    // Use lodash.get to retrieve the URL of the complete logo for the carrier from the COMPLETE_LOGO_CARRIER object.
    // If the carrier is not found in the object, return the COMPLETE_GENERIC_LOGO.
    // Convert the carrier to uppercase before retrieving the logo URL to ensure case-insensitive matching.
    _.get(COMPLETE_CARRIER_LOGO, `${carrier.toUpperCase()}`, CPS_COMPLETE_LOGO)

/**
 * Returns the complete logo for a given flight number.
 * @param flightNumber - The flight number for which to retrieve the logo.
 * @returns The URL of the complete logo for the flight number, or the generic logo if the flight number is not found.
 */
const getLogoByFlightNumber = (
    COMPLETE_CARRIER_LOGO: object = {},
    flightNumber: string = '',
    carrier: string = ''
): string => {
    // Get the first two characters of the flight number and convert them to uppercase.
    const carrierCode = `${flightNumber?.substring(0, 2).toUpperCase()}`

    if (carrier === carrierCode || (carrier === 'AF-KL' && ['AF', 'KL'].includes(carrierCode))) {
        // Create an object to handle edge case carrier logos.
        const edgeCaseCarrierLogoCheck = {
            KL: _.get(COMPLETE_CARRIER_LOGO, 'AF-KL', COMPLETE_GENERIC_LOGO), // Handle KL With AF-KL logo
            AF: _.get(COMPLETE_CARRIER_LOGO, 'AF-KL', COMPLETE_GENERIC_LOGO), // Handle AF With AF-KL logo
            CPS: CPS_COMPLETE_LOGO // Handle CPS Logo
        }

        // Use lodash.get to retrieve the URL of the logo for the carrier code from the COMPLETE_LOGO_CARRIER object.
        // If the carrier code is not found in the object, check the edge case object for the logo.
        // If the carrier code is not found in the edge case object, return the COMPLETE_GENERIC_LOGO.
        // If the carrier code is not found in the object, return the COMPLETE_GENERIC_LOGO.
        return _.get(
            COMPLETE_CARRIER_LOGO,
            carrierCode,
            _.get(edgeCaseCarrierLogoCheck, carrierCode, COMPLETE_GENERIC_LOGO)
        )
    }

    return COMPLETE_GENERIC_LOGO
}

export {
    COMPLETE_GENERIC_LOGO,
    CPS_COMPLETE_LOGO,
    SMALL_GENERIC_LOGO,
    getLogoByCarrier,
    getLogoByCarriernotification,
    getLogoByFlightNumber,
    getSmallLogoByCarrier,
    getSmallLogoByFlightNumber
}
